import React from 'react';

import { Action, Resource } from '~/api/graphql';
import {
  type EmailDraftTemplate,
  type EmailTemplateChoice,
  getEmailDraftTemplate,
  getEmailTemplateChoices
} from '~/api/data/email-templates/queries';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { usePermissions } from '~/components/permissions';

export type DraftTemplateState =
  | { status: 'loading' }
  | { status: 'loaded'; template: EmailDraftTemplate }
  | { status: 'hidden' }
  | { status: 'failed' };

/**
 * The templates the composer offers and the one this email was drafted from. Nothing loads,
 * and `canUseTemplates` is false, while the gate is off or the caller can't list
 * templates.
 */
export const useComposerEmailTemplates = (templateId: string | undefined) => {
  const isFlagEnabled = useFlag(FEATURE_FLAGS.EMAIL_TEMPLATES);
  const canListTemplates = usePermissions().hasPermission(Resource.EmailTemplate, Action.List);
  const canUseTemplates = isFlagEnabled && canListTemplates;

  // Undefined until loaded. A list that fails to load stays undefined until a retry loads it.
  const [choices, setChoices] = React.useState<EmailTemplateChoice[]>();
  const [hasChoicesLoadFailed, setHasChoicesLoadFailed] = React.useState(false);
  const [choicesLoadAttempt, setChoicesLoadAttempt] = React.useState(0);
  React.useEffect(() => {
    if (!canUseTemplates) return;
    let isCurrentRequest = true;
    setHasChoicesLoadFailed(false);
    const markChoicesFailed = () => {
      if (isCurrentRequest) setHasChoicesLoadFailed(true);
    };
    getEmailTemplateChoices()
      .then((result) => {
        if (result.error) return markChoicesFailed();
        if (isCurrentRequest) setChoices(result.data ?? []);
      })
      .catch(markChoicesFailed);
    return () => {
      isCurrentRequest = false;
    };
  }, [canUseTemplates, choicesLoadAttempt]);
  const retryChoices = () => setChoicesLoadAttempt((attempt) => attempt + 1);

  const [draftTemplate, setDraftTemplate] = React.useState<DraftTemplateState>({
    status: 'loading'
  });
  React.useEffect(() => {
    if (!canUseTemplates || !templateId) return;
    let isCurrentRequest = true;
    setDraftTemplate({ status: 'loading' });
    getEmailDraftTemplate(templateId)
      .then((template) => {
        if (!isCurrentRequest) return;
        setDraftTemplate(
          template === 'hidden' ? { status: 'hidden' } : { status: 'loaded', template }
        );
      })
      .catch(() => {
        if (isCurrentRequest) setDraftTemplate({ status: 'failed' });
      });
    return () => {
      isCurrentRequest = false;
    };
  }, [canUseTemplates, templateId]);

  return { canUseTemplates, choices, hasChoicesLoadFailed, retryChoices, draftTemplate };
};
